import {
  QueryClient,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import { router } from "../main";
import { clearFlashMessage, setFlashMessage } from "./flash";
import { z } from "zod";
import {
  AdminPedidoZod,
  AdminStatusZod,
  type AppStatus,
  AppStatusZod,
  ConfigZod,
  type Foto,
  type Usuario,
  UsuarioZod,
} from "./types";
import { GET } from "./api";

const _global = globalThis as any;

// para lidiar con HMR, ver https://chatgpt.com/c/68862b7f-5be8-8009-8945-c0bc3d6de901
export const queryClient: QueryClient =
  _global.__queryClient ?? (_global.__queryClient = new QueryClient());

export const server = import.meta.env.VITE_SERVER || "";

export const useConfig = () => useURL("/config.json", ConfigZod);
export const useAppStatus = () => useURL("/appStatus.json", AppStatusZod);
export const useAdminStatus = () => useURL("/admin.json", AdminStatusZod);

export const usePedido = (id: number) =>
  useQuery({
    queryKey: [`/admin/pedido/${id}`],
    queryFn: () => GET("/admin/pedido/:id", { p: { id } }),
    throwOnError: true,
  });

export const mustLoginError = new Error("must login!");

export function useURL<TSchema extends z.ZodTypeAny>(
  url: string | null,
  parser: TSchema,
  refetchInterval?: number
): UseQueryResult<z.infer<TSchema>> {
  return useQuery({
    queryKey: [url],
    queryFn: async () => {
      if (!url) return new Promise<z.infer<TSchema>>(() => {});

      const res = await fetch(`${server}${url}`, { credentials: "include" });
      const body = await res.json();
      if (mustLogin(body)) throw mustLoginError;

      try {
        return parser.parse(body);
      } catch (e) {
        console.log(Array.isArray(e) ? e.slice(0, 10) : e);
        throw e;
      }
    },
    throwOnError: true,
    refetchInterval,
  });
}

// TODO: acomodar el optimistic, agregar parser
export function POST(
  path: string,
  body?: unknown,
  optimisticUsuario?: (currentUsuario: Usuario) => Usuario
) {
  const mutationKey = ["usuarioPost"];
  const key = ["/appStatus.json"];

  const mutation = queryClient.getMutationCache().build(queryClient, {
    mutationKey,
    mutationFn: async () => {
      const prevData = queryClient.getQueryData(key) as AppStatus | undefined;

      // --- Optimistic Update ---
      if (optimisticUsuario && prevData)
        queryClient.setQueryData(
          key,
          setUsuario(prevData, optimisticUsuario(prevData.state.usuario))
        );

      try {
        clearFlashMessage();
        const response = await (
          await fetch(`${server}${path}`, {
            method: "POST",
            credentials: "include",
            ...(body
              ? {
                  body: JSON.stringify(body),
                  headers: { "Content-Type": "application/json" },
                }
              : {}),
          })
        ).json();

        if (mustLogin(response)) {
          const _global = globalThis as any;
          _global.afterLogin = () => POST(path, body, optimisticUsuario);
          router.navigate({ to: "/login" });
        }

        const usuario = z.object({ usuario: UsuarioZod }).safeParse(response)
          .data?.usuario;
        if (usuario)
          queryClient.setQueryData(key, (old: AppStatus | undefined) =>
            setUsuario(old || baseAppStatus, usuario)
          );

        // TODO: esto no sirve mas, me parece
        const pedido = z.object({ pedido: AdminPedidoZod }).safeParse(response)
          .data?.pedido;
        if (pedido) {
          queryClient.setQueryData([`/admin/pedido/${pedido.id}`], () => ({
            pedido,
          }));
        }

        const flash = z
          .object({
            flash: z
              .array(
                z.tuple([z.enum(["warning", "success", "danger"]), z.string()])
              )
              .nonempty(),
          })
          .safeParse(response).data?.flash;
        if (flash)
          setFlashMessage({
            level: flash[0][0],
            message: flash[0][1],
          });

        const error = z.object({ error: z.string() }).safeParse(response)
          .data?.error;
        if (error)
          setFlashMessage({
            level: "danger",
            message: response.error,
          });

        return response;
      } catch (err) {
        const error = z
          .object({ err: z.object({ message: z.string() }) })
          .safeParse(err).data?.err.message;
        setFlashMessage({
          level: "danger",
          message: error || "Error",
        });
        queryClient.invalidateQueries({ queryKey: key });
        throw err;
      }
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  // Start the mutation
  return mutation.execute(undefined);
}

function setUsuario(prevData: AppStatus, usuario: Usuario): AppStatus {
  return {
    ...prevData,
    state: {
      ...prevData.state,
      usuario,
    },
  };
}

export async function comprarFoto(
  foto: Foto,
  fotos: Foto[],
  multi: boolean,
  encargadas: Record<string, number>
) {
  const comprar = multi
    ? (() => {
        const idx = fotos.findIndex((f) => f.i === foto.i);
        if (idx < 0) return [];
        let start = 0;
        for (let i = 0; i < idx; i++) {
          if (encargadas[fotos[i].i]) start = i;
        }
        return fotos.slice(start + 1, idx + 1);
      })()
    : [foto];

  POST("/comprar_fotos", { fotos: comprar.map((f) => f.i) }, (usuario) => {
    const res =
      usuario.carrito && usuario.pedidos[usuario.carrito]
        ? {
            ...usuario,
            pedidos: {
              ...usuario.pedidos,
              [usuario.carrito]: {
                ...usuario.pedidos[usuario.carrito],
                fotos: [
                  ...usuario.pedidos[usuario.carrito].fotos,
                  ...comprar.map((f) => ({ ...f, cantidad: 1 })),
                ],
              },
            },
            encargadas: {
              ...usuario.encargadas,
              ...Object.fromEntries(comprar.map((f) => [f.i, 1])),
            },
          }
        : usuario;
    return res;
  });
}

export async function comprarPedido(
  id: number,
  formaPago: string,
  obs: string
) {
  const res = await POST(`/comprar/${formaPago}`, { pedido_id: id, obs });
  if ("redirect" in res) window.location = res.redirect;
  else router.navigate({ to: "/" });
}

export async function logout() {
  await POST("/logout");
  router.navigate({ to: "/" });
}

export function setCantidad(
  pedido: number,
  fotoId: number,
  cantidad: number,
  pref = ""
) {
  return POST(
    `${pref}/pedido/set_cantidad?pedido_id=${pedido}&foto_id=${fotoId}&cantidad=${cantidad}`
  );
}

export function selectProducto(
  pedido: number,
  fotoId: number,
  productoId: number,
  pref = ""
) {
  return POST(
    `${pref}/pedido/select_producto?pedido_id=${pedido}&foto_id=${fotoId}&producto_id=${productoId}`
  ); // deberian ir al body.
}

function conforms<T extends z.ZodTypeAny>(
  schema: T,
  value: unknown
): value is z.infer<T> {
  return schema.safeParse(value).success;
}

export function mustLogin(res: unknown) {
  return conforms(z.object({ mustlogin: z.literal(true) }), res);
}

// UGH
const baseAppStatus: AppStatus = {
  state: {
    usuario: {
      id: null,
      carrito: null,
      compradas: null,
      email: null,
      defpass: true,
      encargadas: {},
      nombre: null,
      pedidos: {},
    },
  },
  temporadas: [],
};
